import type { ServiceProblem, ServiceProblemDetail } from '~/types/report-service'

export type ReportScopeErrorReason = 'empty' | 'singleAsset'

export class ReportScopeError extends Error {
  readonly reason: ReportScopeErrorReason
  readonly count: number

  constructor(reason: ReportScopeErrorReason, count?: number) {
    super(reason === 'singleAsset' ? `report scope needs exactly one asset, got ${count ?? 0}` : 'empty report scope')
    this.name = 'ReportScopeError'
    this.reason = reason
    this.count = count ?? 0
  }
}

export class ReportServiceError extends Error {
  readonly status: number | undefined
  readonly title: string | undefined
  readonly detail: string | undefined
  readonly errors: ServiceProblemDetail[]

  constructor(message: string, problem: ServiceProblem, status: number | undefined) {
    super(message)
    this.name = 'ReportServiceError'
    this.status = status
    this.title = problem.title
    this.detail = problem.detail
    this.errors = problem.errors ?? []
  }
}

interface FetchLikeError {
  message?: string
  status?: number
  statusCode?: number
  response?: { status?: number; _data?: unknown }
  data?: unknown
}

const asProblem = (data: unknown): ServiceProblem | null =>
  data && typeof data === 'object' && ('title' in data || 'detail' in data || 'status' in data)
    ? (data as ServiceProblem)
    : null

export const isReportScopeError = (error: unknown): error is ReportScopeError => error instanceof ReportScopeError

export const isReportServiceError = (error: unknown): error is ReportServiceError => error instanceof ReportServiceError

export const toReportServiceError = (error: unknown): Error => {
  if (error instanceof ReportScopeError || error instanceof ReportServiceError) return error
  if (!error || typeof error !== 'object') return new Error(String(error))
  const candidate = error as FetchLikeError
  const status = candidate.response?.status ?? candidate.status ?? candidate.statusCode
  const problem = asProblem(candidate.response?._data) ?? asProblem(candidate.data)
  if (!problem) return error instanceof Error ? error : new Error(candidate.message ?? 'Report service request failed')
  const details = (problem.errors ?? []).map((entry) => entry.message).filter(Boolean)
  const message = problem.detail || details.join('; ') || problem.title || candidate.message || 'Report service error'
  return new ReportServiceError(message, problem, status ?? problem.status)
}
